/**
 * The reading column shared by every chat surface.
 *
 * Chat content is capped at a comfortable reading width and centred. Without
 * it, bubbles collapse to their content width on a desktop viewport and the
 * conversation reads as a narrow left-aligned strip with most of the screen
 * empty — and the input, spanning the full width underneath, floats outside
 * the conversation it belongs to.
 *
 * It exists as a component rather than a copied class string because the
 * copying is what failed: Session and ChatConnected carried the literal,
 * /onboarding was built on the same MessageList + ChatInput pair and never
 * inherited it, so the first screen a new nurse ever sees was the one
 * rendering unframed. A surface now opts in by wrapping, and the width is
 * defined once for all of them.
 *
 * Wrap the CONTENT, never the scrolling element: the scrollbar belongs at the
 * viewport edge, and a full-bleed border or sticky background should still
 * span the full width with only its inner content constrained.
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface ChatReadingColumnProps {
  children: ReactNode;
  /** Vertical rhythm for this instance (e.g. `py-4`); the width is not overridable. */
  className?: string;
}

export function ChatReadingColumn({ children, className }: ChatReadingColumnProps) {
  // 4xl (~896px) keeps line length in the 50–75 character range that reads
  // comfortably for clinical content.
  //
  // w-full is load-bearing, not redundant with the block-level default: as a
  // direct child of a flex column (the /onboarding page shell) the auto side
  // margins CANCEL align-items: stretch, so the box falls back to shrink-to-fit
  // and collapses to its content — a progress bar at 0% collapsed to 32px.
  // Declaring the width first, then capping it, behaves the same in both
  // layouts so a caller never has to know which one it is in.
  return (
    <div className={cn('w-full mx-auto max-w-4xl px-4', className)}>{children}</div>
  );
}
